import { Tooltip } from 'antd';
import { LoadingContainer } from 'app/components/LoadingContainer';
import RenderIf from 'app/components/RenderIf';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { formatNumber, renderReplaceText } from 'utils/helper';
import { IRankingItem } from '../constants';
import ReportEmpty from './ReportEmpty';
import {
  formatCurrencyShort,
  ReportCardHeader,
  REPORT_MOBILE_WIDTH,
} from './reportChartUtils';
import ReportPeriodFilter, { IReportPeriod } from './ReportPeriodFilter';

export interface IRankColorScale {
  top: string;
  middle: string;
  bottom: string;
}

export const ORANGE_SCALE: IRankColorScale = {
  top: 'var(--color-9)',
  middle: '#F6A86F',
  bottom: '#FAD3B5',
};

export const BLUE_SCALE: IRankColorScale = {
  top: 'var(--color-14)',
  middle: '#74BBFF',
  bottom: '#BBDEFF',
};

export const getRankColor = (rank: number, scale: IRankColorScale): string => {
  if (rank <= 3) return scale.top;
  if (rank <= 6) return scale.middle;
  return scale.bottom;
};

const BADGE_GREY_BG = '#F0F0F0';
const BADGE_GREY_TEXT = '#595959';
const VALUE_CHAR_WIDTH = 8.5;
const MIN_VALUE_WIDTH = 70;

interface ITopRankingReportProps {
  translations: any;
  title: string;
  data: IRankingItem[];
  colorScale: IRankColorScale;
  period: IReportPeriod;
  onChangePeriod: (period: Partial<IReportPeriod>) => void;
  isLoading?: boolean;
}

export function TopRankingReport(props: ITopRankingReportProps) {
  const {
    translations,
    title,
    data,
    colorScale,
    period,
    onChangePeriod,
    isLoading,
  } = props;

  const maxRevenue = Math.max(...data.map(item => item.revenue), 0);

  const valueLabels = useMemo(
    () => data.map(item => formatCurrencyShort(item.revenue, 2)),
    [data],
  );
  const valueWidth = Math.max(
    MIN_VALUE_WIDTH,
    Math.ceil(
      Math.max(...valueLabels.map(label => label.length), 0) * VALUE_CHAR_WIDTH,
    ),
  );

  return (
    <div className="box-report-card">
      <ReportCardHeader>
        <div className="sub-title">{title}</div>
        <div className="report-card-actions">
          <ReportPeriodFilter
            translations={translations}
            period={period}
            onChange={onChangePeriod}
          />
        </div>
      </ReportCardHeader>
      <RankingContent $fitContent={!isLoading && !_.isEmpty(data)}>
        <LoadingContainer loading={isLoading} minHeight="200px">
          <RenderIf
            condition={!_.isEmpty(data)}
            fallback={<ReportEmpty translations={translations} height={220} />}
          >
            <RankingList
              style={{ '--rank-value-width': `${valueWidth}px` } as any}
            >
              {data.map((item, index) => {
                const rank = index + 1;
                const color = getRankColor(rank, colorScale);
                const percent = maxRevenue
                  ? (item.revenue / maxRevenue) * 100
                  : 0;
                const isTopRank = rank <= 3;
                return (
                  <div className="ranking-row" key={item.id}>
                    <span
                      className="rank-badge"
                      style={{
                        backgroundColor: isTopRank ? color : BADGE_GREY_BG,
                        color: isTopRank ? 'var(--color-13)' : BADGE_GREY_TEXT,
                      }}
                    >
                      {rank}
                    </span>
                    <RankName name={item.name} parkName={item.parkName} />
                    <Tooltip
                      title={
                        <div>
                          <div>
                            {renderReplaceText(
                              translations?.['b2b.txt.revenueValue'],
                              {
                                value: formatNumber(item.revenue, '.'),
                              },
                            )}
                          </div>
                          <div>
                            {renderReplaceText(
                              translations?.['b2b.txt.ticketQuantityReport'],
                              {
                                value: formatNumber(item.ticketCount, '.'),
                              },
                            )}
                          </div>
                        </div>
                      }
                    >
                      <div className="rank-bar-wrapper">
                        <div
                          className="rank-bar"
                          style={{
                            width: `${percent}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                    </Tooltip>
                    <span className="rank-value">{valueLabels[index]}</span>
                  </div>
                );
              })}
            </RankingList>
          </RenderIf>
        </LoadingContainer>
      </RankingContent>
    </div>
  );
}

const RankName = ({ name, parkName }: { name: string; parkName?: string }) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  const checkEllipsis = () => {
    const element = nameRef.current;
    if (element) setIsEllipsis(element.scrollWidth > element.clientWidth);
  };

  useEffect(checkEllipsis, [name]);

  return (
    <div className="rank-name-wrapper">
      <Tooltip title={isEllipsis ? name : ''}>
        <div className="rank-name" ref={nameRef} onMouseEnter={checkEllipsis}>
          {name}
        </div>
      </Tooltip>
      <RenderIf condition={!!parkName}>
        <div className="rank-park-name">{parkName}</div>
      </RenderIf>
    </div>
  );
};

const RankingContent = styled.div<{ $fitContent: boolean }>`
  ${({ $fitContent }) =>
    $fitContent &&
    css`
      > div,
      .ant-spin {
        min-height: 0;
      }
    `}
`;

const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  .ranking-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  .rank-badge {
    width: 22px;
    height: 22px;
    border-radius: 4px;
    color: var(--color-13);
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 22px;
  }
  .rank-name-wrapper {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .rank-name {
    min-width: 0;
    font-size: 14px;
    color: var(--color-11);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rank-park-name {
    min-width: 0;
    font-size: 14px;
    color: var(--color-12);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rank-bar-wrapper {
    flex: 2 1 0;
    min-width: 60px;
    height: 18px;
    display: flex;
    align-items: center;
    cursor: pointer;
    background-color: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
  }
  .rank-bar {
    height: 100%;
    border-radius: 4px;
    min-width: 2px;
    transition: width 0.3s ease;
  }
  .rank-value {
    flex: 0 0 var(--rank-value-width, 70px);
    width: var(--rank-value-width, 70px);
    min-width: 0;
    text-align: right;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-11);
  }
  /* Mobile: xuống 2 dòng - hạng + tên + giá trị ở trên, thanh bar full width ở dưới */
  @media (max-width: ${REPORT_MOBILE_WIDTH}px) {
    gap: 14px;
    .ranking-row {
      flex-wrap: wrap;
      column-gap: 8px;
      row-gap: 6px;
    }
    .rank-badge {
      order: 1;
    }
    .rank-name-wrapper {
      order: 2;
      flex: 1 1 auto;
    }
    .rank-value {
      order: 3;
      flex: 0 0 auto;
      width: auto;
      font-size: 13px;
    }
    .rank-bar-wrapper {
      order: 4;
      flex: 1 1 100%;
      min-width: 0;
      height: 14px;
    }
  }
`;

export default TopRankingReport;
